import type { FieldProposal } from '../../types';

/**
 * Field-change classification for the canonical change indicator.
 * Pulled out of DealsPage.changeIndicator.tsx so the function lives
 * separately from the component (react-refresh/only-export-components).
 *
 * Spec: docs/archive/pre-cut-2026-08/phase-b-deals-card-fields-spec.md §6.1.
 */

export type ChangeKind = 'add' | 'edit' | 'remove' | null;

function _isEmpty(v: unknown): boolean {
  return v === null || v === undefined || v === '' ||
    (Array.isArray(v) && v.length === 0);
}

/** Classify a FieldProposal as add / edit / remove / no-change.
 *  - no `decision` or `decision='no_change'` → null
 *  - prior empty, proposed non-empty → 'add'
 *  - prior non-empty, proposed empty → 'remove'
 *  - prior and proposed both non-empty + different → 'edit'
 *  - identical values → null (no visual cue) */
export function classifyChange(field: FieldProposal): ChangeKind {
  if (!field.decision || field.decision === 'no_change') return null;

  const priorEmpty = _isEmpty(field.prior_value);
  const proposedEmpty = _isEmpty(field.proposed_value);

  if (priorEmpty && !proposedEmpty) return 'add';
  if (!priorEmpty && proposedEmpty) return 'remove';
  if (priorEmpty && proposedEmpty) return null;

  // Both non-empty — compare by JSON serialization to handle arrays/objects.
  if (JSON.stringify(field.prior_value) === JSON.stringify(field.proposed_value)) {
    return null;
  }
  return 'edit';
}
